// Packages and components
import React, { useCallback, ReactElement } from 'react';
import { Box } from 'grommet';
import { useHistory } from 'react-router-dom';

import SHDButton from '@ui/Button/SHDButton';
import SHDThemeOverride from '@ui/ThemeOverride/SHDThemeOverride';
import { FormAdd } from 'grommet-icons';

import CreateNewGame from './CreateNewGame';
import CameraSelectionSheet from './CameraSelectionSheet';

// Helpers and utilities

// Hooks and contexts
import { useTray } from '@contexts/TrayContext';
import useEventsGameStart from '@hooks/useEventsGameStart';

// Types and interfaces
import { GameStartEvent, GameActionBarProps } from './schedule.d';

const GameActionBar: React.FC<GameActionBarProps> = ({
  teamId,
  teamName,
  sportType,
  isIScore,
  hasRoster,
}) => {
  // Contexts
  const { showTray: showCreateGameTray, hideTray: hideCreateGameTray } =
    useTray();

  const { trackGameStartEvent } = useEventsGameStart(teamId, sportType);

  const history = useHistory();

  const hideGameTray = useCallback(
    (abandoned: boolean = true) => {
      if (abandoned) {
        trackGameStartEvent(GameStartEvent.GAME_CREATION_ABANDONED);
      }
      hideCreateGameTray();
    },
    [hideCreateGameTray, trackGameStartEvent]
  );

  // Handlers
  const handleButtonClick = useCallback(
    (component: ReactElement) => {
      trackGameStartEvent(GameStartEvent.ADD_GAME_CLICKED, {
        hasRoster,
        hasDestination: false,
      });
      showCreateGameTray(component, { onClose: hideGameTray });
    },
    [showCreateGameTray, hideGameTray, hasRoster, trackGameStartEvent]
  );

  const buttonProps = useCallback(() => {
    return isIScore
      ? {
          label: 'Go live',
          onClick: () =>
            handleButtonClick(
              <CameraSelectionSheet
                teamId={teamId}
                teamName={teamName}
                onCloseTray={hideGameTray}
                onClick={(type) => {
                  hideGameTray();
                  if (type === 'settings') {
                    history.push({
                      pathname: `/team_settings/camera`,
                    });
                  }
                }}
              />
            ),
        }
      : {
          label: 'Add game',
          icon: <FormAdd color="white" size="20px" />,
          onClick: () =>
            handleButtonClick(
              <CreateNewGame
                teamId={teamId as string}
                sportType={sportType}
                onClose={hideGameTray}
              />
            ),
        };
  }, [
    isIScore,
    handleButtonClick,
    teamId,
    sportType,
    hideGameTray,
    history,
    teamName,
  ]);

  return (
    <SHDThemeOverride full={false} background="transparent">
      <Box
        data-testid="game-action-bar"
        style={{
          display: 'flex',
          justifyContent: 'center',
          position: 'fixed',
          bottom: 'env(safe-area-inset-bottom, 16px)',
          left: 0,
          right: 0,
          zIndex: 10,
          opacity: 1,
        }}
      >
        <Box
          background="transparent"
          width="100%"
          align="center"
          pad="medium"
          direction="row"
          gap="small"
          justify="center"
        >
          <Box
            data-testid={isIScore ? 'go-live-button' : 'add-game-button'}
            width="144px"
            align="center"
          >
            <SHDButton {...buttonProps()} primary pill />
          </Box>
        </Box>
      </Box>
    </SHDThemeOverride>
  );
};

export default GameActionBar;
